import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, fmtErr, imgUrl } from "@/api";
import { useLang } from "@/i18n";
import ImagePicker from "@/components/ImagePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function SlideshowTab() {
  const { t } = useLang();
  const [slides, setSlides] = useState([]);
  const [image, setImage] = useState([]);
  const [caption, setCaption] = useState("");
  const [captionEn, setCaptionEn] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/slideshow").then((r) => setSlides(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!image.length) return toast.error("Gambar wajib diisi");
    setBusy(true);
    try {
      await api.post("/slideshow", { image: image[0], caption, caption_en: captionEn });
      toast.success(t("common.saved"));
      setImage([]); setCaption(""); setCaptionEn("");
      load();
    } catch (err) { toast.error(fmtErr(err)); }
    setBusy(false);
  };

  const remove = async (sid) => {
    if (!window.confirm(t("common.confirm_delete"))) return;
    try {
      await api.delete(`/slideshow/${sid}`);
      toast.success(t("common.deleted"));
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div data-testid="slideshow-tab">
      <p className="text-sm text-slate-500 mb-6">{slides.length}/7 — 21:9</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        {slides.map((s) => (
          <div key={s.id} className="relative group rounded-lg overflow-hidden border border-slate-200" data-testid={`slide-item-${s.id}`}>
            <img src={imgUrl(s.image)} alt={s.caption} className="aspect-[21/9] w-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3">
              <p className="text-xs text-white font-medium truncate">{s.caption}</p>
            </div>
            <button onClick={() => remove(s.id)} data-testid={`slide-delete-${s.id}`} className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      {slides.length < 7 && (
        <form onSubmit={add} className="bg-white rounded-xl border border-emerald-900/10 p-6 max-w-xl space-y-4" data-testid="slide-add-form">
          <div>
            <Label className="text-sm font-medium text-slate-700">Gambar (rasio 21:9)</Label>
            <div className="mt-1.5"><ImagePicker values={image} onChange={setImage} max={1} testId="slide-image-picker" /></div>
          </div>
          <div>
            <Label className="text-sm font-medium text-slate-700">Keterangan (ID)</Label>
            <Input value={caption} onChange={(e) => setCaption(e.target.value)} className="mt-1.5" data-testid="slide-caption-input" />
          </div>
          <div>
            <Label className="text-sm font-medium text-slate-700">Keterangan (EN, {t("common.optional")})</Label>
            <Input value={captionEn} onChange={(e) => setCaptionEn(e.target.value)} className="mt-1.5" data-testid="slide-caption-en-input" />
          </div>
          <Button type="submit" disabled={busy} className="rounded-full bg-emerald-800 hover:bg-emerald-900" data-testid="slide-add-submit">
            <Plus className="h-4 w-4 mr-2" /> {busy ? t("common.loading") : t("common.add")}
          </Button>
        </form>
      )}
    </div>
  );
}
